import { useEffect, useState } from "react";
import { Gauge as GaugeIcon } from "lucide-react";
import { api } from "@/lib/api";
import { MONTHS } from "@/lib/format";

const pct = (v) => (v === null || v === undefined ? "-" : `${(v * 100).toFixed(2)}%`);
const pts = (v) => (v === null || v === undefined ? "-" : `${v >= 0 ? "+" : ""}${v.toFixed(2)} pt`);

const Gauge = ({ occ, bep, size = 150, muted = false, testid }) => {
  const r = size / 2 - 10, cx = size / 2, cy = size / 2 + 4;
  const clamp = (v) => Math.max(0, Math.min(1.2, v ?? 0));
  const ang = (v) => Math.PI - (clamp(v) / 1.2) * Math.PI;
  const pt = (v, rad) => [cx + rad * Math.cos(ang(v)), cy - rad * Math.sin(ang(v))];
  const arc = (from, to, rad) => {
    const [x1, y1] = pt(from, rad), [x2, y2] = pt(to, rad);
    return `M ${x1} ${y1} A ${rad} ${rad} 0 ${to - from > 0.6 ? 1 : 0} 1 ${x2} ${y2}`;
  };
  const ok = occ !== null && bep !== null && occ >= bep;
  const [nx, ny] = pt(occ, r - 6);
  const [bx1, by1] = pt(bep, r - 16), [bx2, by2] = pt(bep, r + 6);
  const color = occ === null || bep === null ? "#94A3B8" : ok ? "#059669" : "#DC2626";
  return (
    <svg width={size} height={size / 2 + 16} viewBox={`0 0 ${size} ${size / 2 + 16}`} data-testid={testid} className={muted ? "opacity-70" : ""}>
      <path d={arc(0, 1.2, r)} stroke="#E2E8F0" strokeWidth={size > 100 ? 12 : 7} fill="none" strokeLinecap="round" />
      {bep !== null && <path d={arc(0, bep, r)} stroke="#FCA5A5" strokeWidth={size > 100 ? 12 : 7} fill="none" strokeLinecap="round" />}
      {bep !== null && <path d={arc(bep, 1.2, r)} stroke="#A7F3D0" strokeWidth={size > 100 ? 12 : 7} fill="none" strokeLinecap="round" />}
      {bep !== null && <line x1={bx1} y1={by1} x2={bx2} y2={by2} stroke="#B45309" strokeWidth={2} />}
      {occ !== null && <line x1={cx} y1={cy} x2={nx} y2={ny} stroke={color} strokeWidth={size > 100 ? 3 : 2} strokeLinecap="round" />}
      <circle cx={cx} cy={cy} r={size > 100 ? 5 : 3} fill={color} />
    </svg>
  );
};

export const BepCard = ({ projectId, compact = false }) => {
  const [d, setD] = useState(null);
  const [basis, setBasis] = useState("ebitda");
  useEffect(() => { api.get(`/projects/${projectId}/bep`).then((r) => setD(r.data)).catch(() => setD({ available: false })); }, [projectId]);
  if (!d) return null;
  if (!d.available) return <p className="text-xs text-slate-500" data-testid="bep-unavailable">{d.reason || "BEP analysis unavailable"}</p>;
  const k = `bep_${basis}`, mk = `margin_${basis}`;
  const ytdMargin = d.ytd[mk];
  const months = compact ? d.months.filter((m) => m.is_actual).slice(-6) : d.months;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid="bep-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1"><GaugeIcon size={12} className="text-emerald-600" /> Break-even analysis</p>
          <h3 className="font-heading text-lg font-semibold text-slate-900 mt-0.5">Occupancy vs break-even occupancy ({basis.toUpperCase()})</h3>
          <p className="text-xs text-slate-500 mt-1">Safety margin = actual occupancy − BEP occupancy. Months without actuals show the budget (muted).</p>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid="bep-basis-toggle">
          {["gop", "ebitda"].map((b) => <button key={b} onClick={() => setBasis(b)} disabled={!d.labels[b]} data-testid={`bep-basis-${b}`} className={`px-3 py-1 rounded ${basis === b ? "bg-slate-900 text-white" : "text-slate-600 disabled:opacity-40"}`}>{b.toUpperCase()}</button>)}
        </div>
      </div>
      <div className="mt-4 grid gap-5 lg:grid-cols-[220px_1fr] items-start">
        <div className={`rounded-lg p-4 text-center ${ytdMargin === null ? "bg-slate-50" : ytdMargin >= 0 ? "bg-emerald-50" : "bg-red-50"}`} data-testid="bep-ytd">
          <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">YTD · {d.last_month ? `Jan–${MONTHS[d.last_month - 1]}` : "-"}</p>
          <Gauge occ={d.ytd.occupancy} bep={d.ytd[k]} size={180} testid="bep-ytd-gauge" />
          <p className={`font-heading text-3xl font-bold tabular -mt-2 ${ytdMargin === null ? "text-slate-400" : ytdMargin >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid="bep-ytd-margin">{pts(ytdMargin)}</p>
          <p className="text-xs text-slate-600 mt-1 font-mono">Occ {pct(d.ytd.occupancy)} · BEP {pct(d.ytd[k])}</p>
          <p className="text-[11px] text-slate-500 mt-1">Budget occ {pct(d.ytd.budget_occupancy)}</p>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 xl:grid-cols-6 gap-2" data-testid="bep-months">
          {months.map((m) => {
            const margin = m[mk];
            return (
              <div key={m.month} className={`rounded-md border p-2 text-center ${m.is_actual ? "border-slate-200 bg-white" : "border-dashed border-slate-200 bg-slate-50/60"}`} data-testid={`bep-month-${m.month}`}>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{MONTHS[m.month - 1]}{m.is_actual ? "" : " · bud"}</p>
                <Gauge occ={m.occupancy} bep={m[k]} size={86} muted={!m.is_actual} />
                <p className={`font-mono text-xs font-semibold -mt-1 ${margin === null ? "text-slate-400" : margin >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`bep-month-margin-${m.month}`}>{pts(margin)}</p>
                <p className="text-[10px] text-slate-500 font-mono">{pct(m.occupancy)} / {pct(m[k])}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
